<template>
  <div :class="{ errored: hasError }">
    <div class="font-weight-medium mb-2">
      <span :class="{ 'error--text': hasError }">
        {{ label }} <span v-if="required">*</span>
      </span>
    </div>
    <VuePellEditor
      v-model="text"
      :actions="actions"
      placeholder=""
      default-paragraph-separator="p"
      @input="$emit('input', $event)"
    />
    <div class="v-text-field__details">
      <div class="v-messages theme--light error--text" role="alert">
        <div v-for="error of errorMessages" :key="error" class="v-messages__wrapper">
          <div class="v-messages__message">
            {{ error }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="hint !== ''" class="hint">
      {{ hint }}
    </div>
  </div>
</template>

<script>
import sanitizeHtml from 'sanitize-html';
import {
  mdiFormatBold,
  mdiFormatUnderline,
  mdiFormatItalic,
  mdiFormatListBulletedSquare,
  mdiFormatListNumbered,
} from '@mdi/js';

export default {
  name: 'Editor',
  props: {
    value: {
      required: true,
      validator: (prop) => typeof prop === 'string' || prop === null,
    },
    label: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
  },
  inject: {
    form: {
      default: null,
    },
  },
  data() {
    return {
      text: this.value,
      actions: [
        {
          name: 'bold',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24"><path d="${mdiFormatBold}"></path></svg>`,
        },
        {
          name: 'underline',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24" style="padding-top: 1px;"><path d="${mdiFormatUnderline}"></path></svg>`,
        },
        {
          name: 'italic',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24"><path d="${mdiFormatItalic}"></path></svg>`,
        },
        {
          name: 'olist',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24" style="position: relative; top: -1px;"><path d="${mdiFormatListNumbered}"></path></svg>`,
        },
        {
          name: 'ulist',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="24" width="24" style="position: relative; top: -1px;"><path d="${mdiFormatListBulletedSquare}"></path></svg>`,
        },
      ],
    };
  },
  computed: {
    hasError() {
      return Array.isArray(this.errorMessages) && this.errorMessages.length > 0;
    },
  },
  created() {
    if (this.form) {
      this.form.register(this);
    }
  },
  mounted() {
    this.$el.getElementsByClassName('pell-content')[0].onpaste = () => {
      setTimeout(() => {
        this.text = sanitizeHtml(this.text, {
          allowedTags: ['p', 'b', 'strong', 'u', 'i', 'em', 'ul', 'ol', 'li'],
          allowedAttributes: {},
        });
        this.$emit('input', this.text);
      }, 0);
    };
  },
  beforeDestroy() {
    if (this.form) {
      this.form.unregister(this);
    }
  },
};
</script>

<style lang="scss">
.vp-editor {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 5px;

  ul {
    margin-bottom: 15px;
  }
}
.vp-editor__placeholder {
  top: 52px;
  left: $spacer * 3;
}
.pell-actionbar {
  display: flex;
  align-items: center;
  padding: $spacer $spacer * 3;
  height: 40px;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.24);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}
.pell-content {
  padding: $spacer * 3;
  background: none;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}
.pell-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  margin-right: $spacer * 3;
  path {
    fill: rgba(0, 0, 0, 0.5);
  }
  &-selected {
    path {
      fill: rgba(0, 0, 0, 0.7);
    }
  }
}
@media #{map-get($display-breakpoints, 'md-and-up')} {
  .pell-content {
    @include huntica-nice-scroll;
    height: 400px;
  }
}
@media #{map-get($display-breakpoints, 'lg-and-up')} {
  .pell-content {
    height: 510px;
  }
}
</style>

<style style="scss" scoped>
::v-deep {
  .v-text-field__details {
    margin: 5px;
  }
}
</style>
